import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search,RefreshCw} from "lucide-react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import {
  addTab,
  clearTabState,
  removeTab,
  setActiveTab,
  setStatusFilter,
  setListViewState,
  setAdvancedFilters,
  setDefaultTabs,
} from "../../../redux/slices/shipmentSlice";
import ShipmentDetailsView from "./TabContent/ViewShipment/ShipmentDetailsView";
import { DEFAULT_ADVANCED_FILTERS, filtersOverrideStatus } from "./TabContent/shipmentShared";
import { useTabTotals } from "./TabContent/Utils/useTabTotals";
import { resolveTabFilter } from "./TabContent/Utils/resolveTabFilter";
import {
  useShipmentStatuses,
  toViewStatus,
  toApiStatus,
} from "./TabContent/Utils/shipmentStatuses";
import { OVERVIEW_TAB_CONTENT_TYPE } from "./Overview/overviewWidgets";
import { FiSearch } from "react-icons/fi";

// UI & utility imports
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";


// Our tab components
import ShipmentsTable from "./TabContent/ShipmentTable";
import DropTrailers from "./TabContent/DropTrailers";
import CreateShipmentTab from "./TabContent/CreateShipmentTab";
import ShipmentSearchSubTabs from "./TabContent/SearchShipment/ShipmentSearchSubTabs";
import { useShipmentViewTabs, ShipmentViewTabsProvider, viewTabStateKey } from "./ViewTabs";


const DEFAULT_STATUS_FILTER = "New";
// LGX-1164: carriers' preferred landing status — "New" is shipper-only and no longer
// even shown to carriers in the filter, so they can't default to it like shippers do.
const CARRIER_DEFAULT_STATUS_FILTER = "Tendered";

const ShipmentDetails = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("shipments"));
  const statusFilterFromStore = useSelector((state) => state.shipments.statusFilter);
  const statusFilter =
    statusFilterFromStore === undefined ? DEFAULT_STATUS_FILTER : statusFilterFromStore;
  // alias to avoid any stale references during hot reloads
  const filter = statusFilter;
  // One total per tab: kept-alive tables keep fetching behind the tab on screen,
  // so a single shared count ends up showing whichever of them answered last.
  const { setterFor, totalFor } = useTabTotals();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(false); //Force fetch when search button is clicked
  const [reloading, setReloading] = useState(false);
  const { viewStatuses: statusOptions } = useShipmentStatuses();
  const carrierFilterInitialized = useRef(false);
  const initialStatusFilter = useRef(statusFilterFromStore);
  const listViewState = useSelector((s) => s.shipments.listViewState);
  const prevTabStateKeyRef = useRef(null);

  // Tabs the user pinned from a saved view. Server-backed, so they come back on the
  // next login — unlike Redux tab state, which is wiped by resetReduxState().
  const viewTabsStore = useShipmentViewTabs();
  const { viewTabs, loading: viewTabsLoading } = viewTabsStore;
  const [baseTabs, setBaseTabs] = useState([
    { id: "shipment-list", name: "Shipment List" },
  ]);

  // Pinning the tab you are on converts it: the closable tab goes and the pinned one
  // takes its place, so the only thing that changes on screen is that its X is gone.
  // Without this the user is left with two tabs listing the same shipments.
  const pendingPinnedTabIdRef = useRef(null);

  const addViewTabFromControl = useCallback(
    async (name, config, { fromCurrentView = false } = {}) => {
      const created = await viewTabsStore.addViewTab(name, config);
      if (!created || !fromCurrentView) return created;

      // Only a closable tab is converted. Pinning from the shipment list itself is
      // an addition — that tab isn't the user's to close.
      if (activeTab >= defaultTabsCount) {
        dispatch(removeTab({ tabIndex: activeTab }));
      }
      // The pinned tab reaches the bar only once the view tabs are republished, so
      // it is activated from an effect rather than here.
      pendingPinnedTabIdRef.current = viewTabStateKey(created.id);
      return created;
    },
    [viewTabsStore, activeTab, defaultTabsCount, dispatch]
  );

  // Deleting a pinned tab goes through the tab bar's own control rather than
  // removeTab, so the state it was keeping is cleared here instead.
  const removeViewTabAndItsState = useCallback(
    async (viewTabId) => {
      await viewTabsStore.removeViewTab(viewTabId);
      dispatch(clearTabState({ stateKey: viewTabStateKey(viewTabId) }));
    },
    [viewTabsStore, dispatch]
  );

  const providedViewTabs = useMemo(
    () => ({
      ...viewTabsStore,
      addViewTab: addViewTabFromControl,
      removeViewTab: removeViewTabAndItsState,
    }),
    [viewTabsStore, addViewTabFromControl, removeViewTabAndItsState]
  );

  useEffect(() => {
    const pendingId = pendingPinnedTabIdRef.current;
    if (!pendingId) return;
    const index = tabs.findIndex((t) => t.id === pendingId);
    if (index < 0) return; // not published yet
    pendingPinnedTabIdRef.current = null;
    dispatch(setActiveTab({ index }));
  }, [tabs, dispatch]);

  const viewTabsById = useMemo(
    () => Object.fromEntries(viewTabs.map((t) => [String(t.id), t])),
    [viewTabs]
  );

  const currentTabStateKey = tabs[activeTab]?.id ?? "shipment-list";

  // Restore per-tab quick filter only when switching tabs — not on initial mount,
  // where each tab's saved view owns filter via onApplyConfig.
  //
  // Resolved exactly the way the tables resolve it, module default included. A tab
  // that has never had a filter set of its own lists the default, so the toolbar has
  // to say the default too: reading nothing and leaving the previous tab's filter on
  // screen is what made the shipment list say "All Statuses", after a visit to a tab
  // that was on All Statuses, while it was still listing New.
  useEffect(() => {
    const prevKey = prevTabStateKeyRef.current;
    prevTabStateKeyRef.current = currentTabStateKey;

    if (prevKey === null || prevKey === currentTabStateKey) return;

    dispatch(
      setStatusFilter(
        resolveTabFilter(listViewState, currentTabStateKey, DEFAULT_STATUS_FILTER)
      )
    );
  }, [currentTabStateKey, listViewState, dispatch]);

  const currentTabAdvancedFilters = useSelector(
    (s) => s.shipments.advancedFilters?.[currentTabStateKey] ?? DEFAULT_ADVANCED_FILTERS
  );
  // "All Statuses" only when the tab's rules state a status of their own; a rule
  // about something else leaves the quick filter in force, and the button has to
  // say so or it contradicts what the tab is listing.
  const advancedFiltersOwnStatus = filtersOverrideStatus(currentTabAdvancedFilters);

  // Undefined for a tab that lists nothing — a shipment view — so the toolbar drops
  // the count there instead of claiming zero shipments.
  const activeTabTotal = totalFor(currentTabStateKey);

  // Each table reads its own tab's filter. They are all mounted at once, so passing
  // the module's current filter to every one of them turned each tab switch into a
  // refetch of every other tab — under a filter that wasn't theirs, which took
  // their shipment count and their page with it.
  const filterForTab = (stateKey) =>
    resolveTabFilter(listViewState, stateKey, DEFAULT_STATUS_FILTER);


  const filterButtonValue = advancedFiltersOwnStatus
    ? "All Statuses"
    : (statusFilter ? toViewStatus(statusFilter) : "All Statuses");


  // ✅ Log Redux tabs for debugging
  useEffect(() => {
  }, [tabs]);

  // ✅ Dynamically set default tabs based on org type (no reload needed)
  useEffect(() => {
    try {
      const stored = localStorage.getItem("organizationTypes");
      const parsed = stored ? JSON.parse(stored) : [];
      const normalized = (Array.isArray(parsed) ? parsed : [])
        .map((t) => String(t ?? "").toLowerCase().trim())
        .filter(Boolean);
      const isOnlyShipper =
        normalized.length === 1 && normalized[0] === "shipper";
      const isOnlyCarrier =
        normalized.length === 1 && normalized[0] === "carrier";

      const listOnlyTabs = [{ id: "shipment-list", name: "Shipment List" }];
      const shipperTabs = [
        ...listOnlyTabs,
        { id: "drop-trailers", name: "Drop Trailers" },
      ];

      setBaseTabs(isOnlyShipper || isOnlyCarrier ? shipperTabs : listOnlyTabs);

        // LGX-1164: carriers land on "Tendered" (their actionable default), not the
        // shipper-only "New" — and only when they still have the initial default filter.
        if (
            isOnlyCarrier &&
            !carrierFilterInitialized.current &&
            (initialStatusFilter.current === DEFAULT_STATUS_FILTER || initialStatusFilter.current === undefined)
        ) {
            carrierFilterInitialized.current = true;
            dispatch(setStatusFilter(CARRIER_DEFAULT_STATUS_FILTER));
            dispatch(setListViewState({ stateKey: currentTabStateKey, filter: CARRIER_DEFAULT_STATUS_FILTER }));
        }
    } catch (err) {
        console.error("Failed to set tabs from organizationTypes", err);
    }
  }, [dispatch]);

  // Saved view tabs sit alongside the org's own tabs rather than in dynamicTabs:
  // they're permanent until removed, so they shouldn't count against the 10-tab
  // dynamic cap or be closable by the generic X. The Tabs control owns them.
  useEffect(() => {
    // Nothing is published until they have loaded. Coming back to the module, the
    // base tabs alone would be a tab list without the pinned ones in it, and
    // setDefaultTabs can only keep the user on a tab it can still see — so whoever
    // left on a pinned tab would be moved off it a moment before it reappeared.
    if (viewTabsLoading) return;

    dispatch(
      setDefaultTabs([
        ...baseTabs,
        ...viewTabs.map((t) => ({
          id: t.stateKey,
          name: t.name,
          contentType: "savedViewTab",
          viewTabId: t.id,
        })),
      ])
    );
  }, [dispatch, baseTabs, viewTabs, viewTabsLoading]);

   // ✅ Handle filter change
  //  const handleFilter = (selectedValue) => {
  //   setFilter(selectedValue === "Status" ? null : selectedValue);
  //   setSearchQuery(filter != "Status" ? filter : null); // Clear search when filter changes
  //   handleSearch() // Force fetch with new filter
  // };

  const handleFilter = (selectedViewValue) => {
    const nextApiValue =
      selectedViewValue === "All Statuses" ? null : toApiStatus(selectedViewValue);

    dispatch(setStatusFilter(nextApiValue));            // store API value (Completed/Active)
    dispatch(setListViewState({ stateKey: currentTabStateKey, filter: nextApiValue }));
    setSearchQuery("");                 // clear the free-text search
    setSearchTrigger(prev => !prev);    // force refetch using filter
  };


   // ✅ Clear filters
   const handleClearFilters = () => {
    setSearchQuery("");
    dispatch(setStatusFilter(DEFAULT_STATUS_FILTER));
    dispatch(setListViewState({ stateKey: currentTabStateKey, filter: DEFAULT_STATUS_FILTER }));
    setSearchTrigger((prev) => !prev); // ✅ Force fetch with cleared filters
  };


  // Default content for each tab
  const defaultTabContents = {
    "shipment-list": (
      <ShipmentsTable
        key="shipment-list"
        setTotalShipments={setterFor("shipment-list")}
        filter={filterForTab("shipment-list")}
        searchTrigger={searchTrigger}
        searchQuery=""
        stateKey="shipment-list"
      />
    ),
    "drop-trailers": (
      <DropTrailers
        key="drop-trailers"
        setTotalShipments={setterFor("drop-trailers")}
        filter={filterForTab("drop-trailers")}
        searchTrigger={searchTrigger}
        searchQuery=""
      />
    ),
    // "shipment-form": <ShipmentForm key="shipment-form" />,
  };

  // Navigate to the shipment list tab and force a data refresh.
  // Used after create and after plan/replan so the user immediately sees the
  // updated shipment without having to manually switch tabs or hit refresh.
  const returnToShipmentListWithRefresh = useCallback(() => {
    const listIndex = tabs.findIndex((tab) => tab.id === "shipment-list");
    if (listIndex >= 0) dispatch(setActiveTab({ index: listIndex }));
    setSearchTrigger((prev) => !prev);
  }, [tabs, dispatch]);

  // When clicking "Create Shipment", dispatch a new tab
  const handleCreateShipment = () => {
    dispatch(addTab());
  };

  // Remove tab only if it's dynamic
  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index }));
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const activeDef = tabs[activeTab];
    const baseId =
      activeDef && (activeDef.id === "drop-trailers" || activeDef.id === "shipment-list" || activeDef.id === "missing-appt")
        ? activeDef.id
        : "shipment-list";

    const newTabId = `search-${baseId}-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      name: `Search(${baseId === "drop-trailers" ? "Drop" : "Shipments"}): ${searchQuery}`,
      contentType: "searchResults",
      baseId,
      searchQuery,
      searchTrigger,          // ← snapshot the value right now
      isDynamic: true,
      createdAt: Date.now(),
    }));

    // The tab-limit middleware can refuse the tab, and it toasts when it does —
    // the term stays in the box then, since there is nowhere else it now lives.
    const opened = store
      .getState()
      .shipments.dynamicTabs.some((tab) => tab.id === newTabId);
    if (!opened) return;

    // A search narrows within whatever the tab it was run from was already showing —
    // carry that tab's quick status filter and advanced filters onto the new one, same
    // as reopening that tab would. Nothing active means a global search, since both
    // resolve to "no filter" on their own.
    dispatch(setListViewState({
      stateKey: newTabId,
      filter: filterForTab(currentTabStateKey),
    }));
    dispatch(setAdvancedFilters({
      stateKey: newTabId,
      filters: currentTabAdvancedFilters,
    }));

    dispatch(setActiveTab({ index: tabs.length }));
    // The results are their own tab now, named for the term, so leaving it in the
    // box only invites searching for it twice.
    setSearchQuery("");

  };



  // const handleSearch = () => {
  //   if (!searchQuery.trim()) return;

  //   // Create a unique id for the search tab and provide custom name/content.
  //   const newTabId = `search-${Date.now()}`;
  //   dispatch(addTab({
  //     id: newTabId,
  //     name: `Search: ${searchQuery}`,
  //     contentType: "searchResults",
  //     searchQuery,
  //     isDynamic: true,
  //     createdAt: Date.now(),
  //   }));
  //   dispatch(setActiveTab({ index: tabs.length })); // Activate new tab
  // };

  return (
    <ShipmentViewTabsProvider value={providedViewTabs}>
    <div className="p-fluid-1 shadow-md flex flex-col font-roboto flex-1 h-full min-h-0 overflow-hidden">
    {/* Top Action/Filter Bar */}
    <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
      {/* Left side: Filter buttons, search box */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterButton
          text={filterButtonValue}
          options={["All Statuses", ...statusOptions]}
          selectedValue={filterButtonValue}
          onSelect={handleFilter}
          backgroundColor="white"
          textColor="black"
        />
  
        <SearchBox
          value={searchQuery}
          onChange={setSearchQuery}
          onSearch={handleSearch}
          placeholder="Search Shipments"
        />

        {activeTabTotal !== undefined && (
          <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
            {activeTabTotal} Shipments
          </span>
        )}
        <button
          onClick={() => {
            setReloading(true);           // spin the icon briefly
            setTimeout(() => window.location.reload(), 150); // tiny delay for visual feedback
          }}
          className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
          title="Reload page"
          aria-label="Reload page"
        >
          <RefreshCw
            size={16}
            className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
          />
        </button>
      </div>
  
      {/* Right side: Download button & Create Shipment */}
      <div className="flex items-center gap-2">
        {/* <div className="p-2 rounded-md hover:bg-gray-100 transition cursor-pointer">
          <Download size={24} strokeWidth={2} className="text-[#223688]" />
        </div> */}
  
        <Button
          text="Create Shipment"
          onClick={handleCreateShipment}
          backgroundColor="#223688"
          textColor="white"
          fontSize="14px"
          padding="10px 18px"
          borderRadius="24px"
        />
      </div>
    </div>
  
    {/* Tab Content Area */}
  <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
      <Tabs
        tabs={tabs.map(t => t.name)}
        tabContents={tabs.map((tab) => {
          if (tab.contentType === "searchResults") {
            if (tab.baseId === "drop-trailers") {
              return (
                <DropTrailers
                  key={tab.id}
                  setTotalShipments={setterFor(tab.id)}
                  filter={filterForTab(tab.id)}
                  searchTrigger={tab.searchTrigger} 
                  searchQuery={tab.searchQuery}   // <- use the tab’s query
                  stateKey={tab.id}
                />
              );
            }
            // default: shipments search
            return (
                <ShipmentSearchSubTabs
                  key={tab.id}
                  setTotalShipments={setterFor(tab.id)}
                  searchTrigger={tab.searchTrigger} 
                  filter={filterForTab(tab.id)} 
                  searchQuery={tab.searchQuery}     // <- use the tab’s query
                  stateKey={tab.id}
                />
              );
          }

          if (tab.contentType === "savedViewTab") {
            return (
              <ShipmentsTable
                key={tab.id}
                setTotalShipments={setterFor(tab.id)}
                filter={filterForTab(tab.id)}
                searchTrigger={searchTrigger}
                searchQuery=""
                stateKey={tab.id}
                viewTab={viewTabsById[String(tab.viewTabId)]}
              />
            );
          }

          if (tab.contentType === OVERVIEW_TAB_CONTENT_TYPE) {
            return (
              <ShipmentsTable
                key={tab.id}
                setTotalShipments={setterFor(tab.id)}
                filter={filterForTab(tab.id)}
                searchTrigger={searchTrigger}
                searchQuery=""
                stateKey={tab.id}
                initialConfig={{
                  filter: tab.status ?? null,
                  advancedFilters: tab.advancedFilters,
                }}
              />
            );
          }

          if (tab.contentType === "shipmentView") {
            return (
              <ShipmentDetailsView
                key={tab.shipmentNumber}
                shipmentNumber={tab.shipmentNumber}
                shipmentOfficeCode={tab.shipmentOfficeCode}
                onCarrierAssignmentComplete={returnToShipmentListWithRefresh}
              />
            );
          }

          return (
            defaultTabContents[tab.id] || (
              <CreateShipmentTab
                key={tab.id}
                instanceId={tab.id}
                title={tab.name}
                setActiveTab={(payload) => dispatch(setActiveTab(payload))}
                tabs={tabs}
                onCreated={() => {
                  const tabIndex = tabs.findIndex((t) => t.id === tab.id);
                  if (tabIndex >= defaultTabsCount) dispatch(removeTab({ tabIndex }));
                  returnToShipmentListWithRefresh();
                }}
              />
            )
          );
        })}
        activeTab={activeTab}
        onTabChange={(index) => dispatch(setActiveTab({ index }))}
        isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
        onRemoveTab={handleRemoveTab}
        keepAlive={tabs.map(
          (tab) =>
            tab.contentType === "searchResults" ||
            tab.contentType === OVERVIEW_TAB_CONTENT_TYPE ||
            tab.id === "shipment-list"
        )}
      />
    </div>
  </div>
  </ShipmentViewTabsProvider>
  );
};

export default ShipmentDetails;
